/**
 * update_tester_note Tool — edit an existing tester knowledge-base note (Issue #2661)
 *
 * At least one editable field must be supplied; bare `noteId` calls return an
 * error before any API roundtrip.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';
import type { UpdateTesterNoteRequest, TesterNoteTag } from '@runhuman/shared';

const NOTE_TAGS: readonly TesterNoteTag[] = [
  'credentials',
  'environment',
  'setup',
  'procedures',
  'known-issues',
  'contacts',
  'general',
];

export interface UpdateTesterNoteArgs extends UpdateTesterNoteRequest {
  readonly noteId: string;
}

export const updateTesterNoteToolDefinition: Tool = {
  name: 'update_tester_note',
  description: `Edit an existing tester knowledge-base note by ID.

Pass any combination of editable fields — at least one is required. Use list_tester_notes to discover note IDs.

To re-scope to a different project, pass projectId. To clear project scope (move to org-wide), pass projectId: null.`,
  inputSchema: {
    type: 'object',
    properties: {
      noteId: {
        type: 'string',
        description: 'Note ID to update (required).',
      },
      title: { type: 'string', description: 'New title.' },
      body: { type: 'string', description: 'New body.' },
      organizationId: { type: 'string', description: 'Move the note to a different organization.' },
      projectId: {
        type: ['string', 'null'],
        description: 'Project to scope to. Pass null to clear (move to org-wide).',
      },
      tags: {
        type: 'array',
        items: { type: 'string', enum: NOTE_TAGS as unknown as string[] },
        description: `Replace tags. Allowed: ${NOTE_TAGS.join(', ')}.`,
      },
      isInternal: {
        type: 'boolean',
        description: 'Toggle internal-only visibility (Volter staff only).',
      },
    },
    required: ['noteId'],
  },
};

export async function handleUpdateTesterNote(
  args: UpdateTesterNoteArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  const { noteId, ...updates } = args;

  if (Object.keys(updates).length === 0) {
    return {
      content: [{
        type: 'text',
        text: `Failed to update tester note

Error: Nothing to update. Pass at least one of: title, body, organizationId, projectId, tags, isInternal.`,
      }],
      isError: true,
    };
  }

  try {
    const note = await jobClient.updateTesterNote(noteId, updates);

    return {
      content: [{
        type: 'text',
        text: `Tester Note Updated!

Title: ${note.title}
Note ID: ${note.id}
Org: ${note.organizationName} (${note.organizationId})
Project: ${note.projectId ? `${note.projectName ?? note.projectId} (${note.projectId})` : 'org-wide (no project)'}
Visibility: ${note.isInternal ? 'internal' : 'customer-visible'}${note.tags.length > 0 ? `\nTags: ${note.tags.join(', ')}` : ''}`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to update tester note\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
